import React, { useContext, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    IconButton,
} from "@mui/material";
import { CloudUpload, Close, Info } from "@mui/icons-material";
import "../../assets/styles/UploadOrdersModal.scss";
import { CommonContext, CategoryContext } from "../../context";
import commonApi from "../../utils/api";
import UploadAlert from "../common/UploadAlert";

const UploadCategoryModal = () => {
    const { user } = useContext(CommonContext);
    const { uploadModal, handleCloseUploadModal, onLoadApi } = useContext(CategoryContext);
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [uploadResponse, setUploadResponse] = useState(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const validateFile = (uploadedFile) => {
        if (!uploadedFile) return false;

        if (
            ![
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ].includes(uploadedFile.type)
        ) {
            setError("Only .xls or .xlsx files are allowed.");
            return false;
        }

        if (uploadedFile.size > 10 * 1024 * 1024) {
            setError("File size must be less than 10MB.");
            return false;
        }

        setError("");
        return true;
    };

    const handleFileChange = (e) => {
        const uploadedFile = e.target.files[0];
        if (validateFile(uploadedFile)) {
            setFile(uploadedFile);
        } else {
            setFile(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const uploadedFile = e.dataTransfer.files[0];
            if (validateFile(uploadedFile)) {
                setFile(uploadedFile);
            } else {
                setFile(null);
            }
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            setError("Please upload a valid file.");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append('admin_id', user?.id)

            const res = await commonApi.post("merchant-category/upload", formData);

            if (res?.data) {
                setUploadResponse(res.data);
                setAlertOpen(true);
                setFile(null);
                setError("");
                if (res.data.s === 1) {
                    onLoadApi();
                }
            }
        } catch (err) {
            setError("Something went wrong. Please try again.");
            console.error(err);
        }
    };

    return (
        <>
            <UploadAlert
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                response={uploadResponse}
                label="Category"
            />
            <Modal open={uploadModal}>
                <Box className="upload-orders-modal">
                    <Box className="modal-header">
                        <Typography variant="h6">Upload Categories</Typography>
                        <IconButton onClick={handleCloseUploadModal} size="small">
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="content-body">
                        <Box className="instructions">
                            <Info className="info-icon" />
                            <Box>
                                <Typography className="instructions-title">
                                    Important Instructions
                                </Typography>
                                <Typography className="instructions-text">
                                    Upload all your categories via XLSX. Please download the template below,
                                    remove sample data and upload your data. Make sure you fill at least
                                    the mandatory fields.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    className="download-btn"
                                    startIcon={<CloudUpload />}
                                    onClick={() => window.open("/format/category_sample.xlsx", "_blank")}
                                >
                                    Download Category Format File
                                </Button>
                            </Box>
                        </Box>

                        <Box
                            className={`file-upload ${dragActive ? "drag-active" : ""}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <label htmlFor="category-file-input" className="file-dropzone">
                                <CloudUpload fontSize="large" />
                                <Typography>
                                    {file ? file.name : "Choose file or drag & drop"}
                                </Typography>
                                <Typography variant="body2" className="file-info">
                                    Supported formats: .xls, .xlsx | Max size: 10MB
                                </Typography>
                            </label>
                            <input
                                id="category-file-input"
                                type="file"
                                hidden
                                onChange={handleFileChange}
                            />
                            {error && <Typography className="error-text">{error}</Typography>}
                        </Box>
                    </Box>

                    <Box className="modal-actions">
                        <Button variant="outlined" onClick={handleCloseUploadModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleSubmit}
                            disabled={!file}
                        >
                            Upload Category
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default UploadCategoryModal;
